import React, { useEffect, useState } from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { EmployeeView } from '../../../../services/employees/employees.service';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { sortEmployeesForPicker } from '../auth/employee-sort.utils';
import classes from './fixed-employee-modal.module.scss';

export type FixedEmployeeModalProps = {
  opened: boolean;
  onClose: () => void;
  employees: EmployeeView[];
  fixedEmployeeId: number | null;
  onSelect: (employeeId: number) => void;
  onClear: () => void;
};

// Summary: Manager picker fixing which single employee this device's login gate offers.
const FixedEmployeeModalComponent: React.FC<FixedEmployeeModalProps> = ({
  opened,
  onClose,
  employees,
  fixedEmployeeId,
  onSelect,
  onClear,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(fixedEmployeeId);

  useEffect(() => {
    if (opened) {
      setSelectedId(fixedEmployeeId);
    }
  }, [opened, fixedEmployeeId]);

  const handleSave = () => {
    if (selectedId == null) return;
    onSelect(selectedId);
    onClose();
  };

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={<strong>קיבוע משתמש למכשיר</strong>}
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      zIndex={400}
      closeButtonProps={{ 'data-testid': 'fixed-employee-close' } as Record<string, string>}
    >
      <div className={classes.stack}>
        {employees.length === 0 ? (
          <span className={classes.empty}>אין עובדים פעילים</span>
        ) : (
          <div className={classes.list}>
            {sortEmployeesForPicker(employees).map((employee) => {
              const isSelected = selectedId === employee.id;
              return (
                <UnstyledButton
                  key={employee.id}
                  component="button"
                  type="button"
                  data-testid={`fixed-employee-option-${employee.id}`}
                  className={isSelected ? `${classes.item} ${classes.itemActive}` : classes.item}
                  onClick={() => setSelectedId(employee.id)}
                >
                  <span>{employee.fullName}</span>
                  {isSelected && <FontAwesomeIcon icon={faCheck as IconProp} />}
                </UnstyledButton>
              );
            })}
          </div>
        )}
        <div className={classes.actions}>
          <Button variant="cancel" onClick={handleClear} disabled={fixedEmployeeId == null} dataTestId="fixed-employee-clear">
            נקה / בטל קיבוע
          </Button>
          <Button variant="save" onClick={handleSave} disabled={selectedId == null} dataTestId="fixed-employee-save">
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const FixedEmployeeModal = React.memo(FixedEmployeeModalComponent);
